import { useCallback, useEffect, useState } from "react";

import {
  GetMetadataFieldsResponse,
  MetadataFieldConfig,
} from "@/components/metadata-config/types";
import { useNotifications } from "@/contexts/NotificationsContext";

import { clearAssetsCache } from "../cart/useAssets";
import { useApi } from "../useApi";

/**
 * Data access for the admin "Metadata Configuration" page.
 *
 * Reads the Iconik metadata-field configuration from the admin-gated
 * ``GET /admin/metadata-fields`` endpoint and persists the ``showInPreview``
 * and ``availableToAgent`` governance flags via
 * ``PATCH /admin/metadata-fields/{fieldName}``.
 *
 * ``availableToAgent`` controls which fields the agent may filter on: the
 * text2sql prompt's filter vocabulary is hydrated from the enabled fields, so
 * toggling it changes what metadata the agent can narrow clips by.
 */
const useAdminMetadataFields = () => {
  const [fields, setFields] = useState<MetadataFieldConfig[]>([]);
  const [loading, setLoading] = useState(false);

  const { notifyError } = useNotifications();
  // isAdmin: true makes useApi send the access token the admin authorizer
  // validates; the ID token would be rejected.
  const { apiGet, apiPatch } = useApi({ isAdmin: true });

  const loadFields = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet<GetMetadataFieldsResponse>(
        "/admin/metadata-fields"
      );
      setFields(response?.fields ?? []);
    } catch (error) {
      console.error(error);
      notifyError("Metadata Error", "Failed to load metadata fields");
    } finally {
      setLoading(false);
    }
  }, [apiGet, notifyError]);

  /**
   * Merge a successful PATCH response's attribution stamp
   * (``lastChangedBy``/``lastChangedAt``) into local state.
   *
   * The client never knows its own admin identity ahead of time (the server
   * derives it from the authorizer context on the write), so this stamp
   * cannot be applied optimistically like the boolean flags -- it is only
   * safe to merge once the PATCH has actually succeeded and returned the
   * server-computed value.
   */
  const applyAttributionStamp = useCallback(
    (fieldName: string, patched: Partial<MetadataFieldConfig> | undefined) => {
      if (!patched) return;
      setFields((prev) =>
        prev.map((f) =>
          f.fieldName === fieldName
            ? {
                ...f,
                lastChangedBy: patched.lastChangedBy ?? f.lastChangedBy,
                lastChangedAt: patched.lastChangedAt ?? f.lastChangedAt,
              }
            : f
        )
      );
    },
    []
  );

  /**
   * Persist a field's ``showInPreview`` flag.
   *
   * Optimistic: the toggle flips immediately, then the PATCH is issued. If
   * the request fails the toggle is rolled back to its previous value and an
   * error is surfaced, so the UI never shows a value that did not persist.
   */
  const setShowInPreview = useCallback(
    async (fieldName: string, value: boolean) => {
      setFields((prev) =>
        prev.map((f) =>
          f.fieldName === fieldName ? { ...f, showInPreview: value } : f
        )
      );
      try {
        const response = await apiPatch<{ field: Partial<MetadataFieldConfig> }>(
          `/admin/metadata-fields/${fieldName}`,
          { showInPreview: value }
        );
        // The server derives "who made this change" from the authorizer
        // context -- the client has no way to know its own admin identity
        // ahead of time, so the attribution stamp is adopted from the PATCH
        // response rather than guessed optimistically.
        applyAttributionStamp(fieldName, response?.field);
        // The Clip Preview reads admin-enabled fields baked into the cached
        // /assets responses; invalidate that cache so the change is reflected
        // the next time a play's clips are opened (no hard refresh needed).
        clearAssetsCache();
      } catch (error) {
        console.error(error);
        // Roll back the optimistic flip on failure.
        setFields((prev) =>
          prev.map((f) =>
            f.fieldName === fieldName ? { ...f, showInPreview: !value } : f
          )
        );
        notifyError(
          "Metadata Error",
          `Failed to update "${fieldName}" preview visibility`
        );
      }
    },
    [apiPatch, notifyError, applyAttributionStamp]
  );

  /**
   * Persist a field's ``availableToAgent`` flag.
   *
   * Optimistic, same as {@link setShowInPreview}: flip immediately, PATCH,
   * roll back on failure. No assets-cache invalidation here — this flag
   * governs the agent's filter vocabulary (hydrated into the text2sql prompt
   * per request), not the cached ``/assets`` Clip Preview responses.
   */
  const setAvailableToAgent = useCallback(
    async (fieldName: string, value: boolean) => {
      setFields((prev) =>
        prev.map((f) =>
          f.fieldName === fieldName ? { ...f, availableToAgent: value } : f
        )
      );
      try {
        const response = await apiPatch<{ field: Partial<MetadataFieldConfig> }>(
          `/admin/metadata-fields/${fieldName}`,
          { availableToAgent: value }
        );
        applyAttributionStamp(fieldName, response?.field);
      } catch (error) {
        console.error(error);
        setFields((prev) =>
          prev.map((f) =>
            f.fieldName === fieldName ? { ...f, availableToAgent: !value } : f
          )
        );
        notifyError(
          "Metadata Error",
          `Failed to update "${fieldName}" agent availability`
        );
      }
    },
    [apiPatch, notifyError, applyAttributionStamp]
  );

  // Load on mount.
  useEffect(() => {
    loadFields();
  }, [loadFields]);

  return { fields, loading, loadFields, setShowInPreview, setAvailableToAgent };
};

export default useAdminMetadataFields;
